import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { revenueService, clientService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ChevronLeft, ChevronRight, Save, TrendingUp, TrendingDown, Minus, Plus, Trash2, Users } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const MONTHS = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
const MONTH_FULL = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const PIE_COLORS = ['#0f172a', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

const fmtCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  return new Intl.NumberFormat('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

const fmtPct = (v) => {
  if (v === null || v === undefined || !isFinite(v)) return '-';
  const sign = v >= 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
};

export default function RevenueKPIPage() {
  const { user } = useAuth();
  const canEdit = ['admin', 'management', 'quality'].includes(user?.role);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dirtyTargets, setDirtyTargets] = useState({});
  const [dirtyCustomers, setDirtyCustomers] = useState({});
  const [saving, setSaving] = useState(false);
  const [expandedMonth, setExpandedMonth] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [revRes, cliRes] = await Promise.all([
        revenueService.getByYear(year),
        clientService.getAll(),
      ]);
      setData(revRes.data);
      setClients(cliRes.data);  // load all clients; CustomerBreakdown handles active/inactive filter
      setDirtyTargets({});
      setDirtyCustomers({});
    } catch { toast.error('Errore caricamento dati'); } finally { setLoading(false); }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleTargetChange = (month, field, val) => {
    setData(prev => prev.map(r => r.month === month ? { ...r, [field]: val } : r));
    setDirtyTargets(prev => ({ ...prev, [month]: true }));
  };

  const handleCustomerRevenue = (month, clientId, revenue) => {
    setData(prev => prev.map(r => {
      if (r.month !== month) return r;
      const customers = [...(r.customers || [])];
      const idx = customers.findIndex(c => c.client_id === clientId);
      if (idx >= 0) {
        customers[idx] = { ...customers[idx], revenue: revenue };
      } else {
        // Auto-add the customer entry when user types a value for a previously-empty client
        const cli = clients.find(c => c.id === clientId) || {};
        customers.push({
          id: null, client_id: clientId,
          client_name: cli.name || 'N/D', client_code: cli.code || '',
          revenue: revenue, notes: null,
        });
      }
      // Recalculate actual_value
      const newActual = customers.reduce((sum, c) => sum + (parseFloat(c.revenue) || 0), 0);
      return { ...r, customers, actual_value: newActual > 0 ? newActual : null };
    }));
    setDirtyCustomers(prev => ({ ...prev, [`${month}-${clientId}`]: true }));
  };

  const handleCustomerNotes = (month, clientId, notes) => {
    setData(prev => prev.map(r => {
      if (r.month !== month) return r;
      const customers = [...(r.customers || [])];
      const idx = customers.findIndex(c => c.client_id === clientId);
      if (idx >= 0) {
        customers[idx] = { ...customers[idx], notes };
      } else {
        const cli = clients.find(c => c.id === clientId) || {};
        customers.push({
          id: null, client_id: clientId,
          client_name: cli.name || 'N/D', client_code: cli.code || '',
          revenue: 0, notes,
        });
      }
      return { ...r, customers };
    }));
    setDirtyCustomers(prev => ({ ...prev, [`${month}-${clientId}`]: true }));
  };

  const addCustomerToMonth = (month, clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    setData(prev => prev.map(r => {
      if (r.month !== month) return r;
      if (r.customers?.some(c => c.client_id === clientId)) {
        toast.info('Cliente già presente');
        return r;
      }
      return {
        ...r,
        customers: [...(r.customers || []), {
          id: null, client_id: clientId, client_name: client.name, client_code: client.code, revenue: 0, notes: null
        }]
      };
    }));
    setDirtyCustomers(prev => ({ ...prev, [`${month}-${clientId}`]: true }));
  };

  const removeCustomerFromMonth = async (month, entry) => {
    if (entry.id) {
      try {
        await revenueService.deleteCustomer(entry.id);
      } catch { toast.error('Errore eliminazione'); return; }
    }
    setData(prev => prev.map(r => {
      if (r.month !== month) return r;
      const customers = (r.customers || []).filter(c => c.client_id !== entry.client_id);
      const newActual = customers.reduce((sum, c) => sum + (parseFloat(c.revenue) || 0), 0);
      return { ...r, customers, actual_value: newActual > 0 ? newActual : null };
    }));
    toast.success('Cliente rimosso');
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      // Save dirty targets
      const dirtyTargetMonths = Object.keys(dirtyTargets).map(Number);
      if (dirtyTargetMonths.length > 0) {
        const items = dirtyTargetMonths.map(m => {
          const row = data.find(r => r.month === m);
          return {
            year, month: m,
            target_value: row.target_value !== '' && row.target_value !== null ? parseFloat(row.target_value) : null,
            notes: row.notes || null,
          };
        });
        await revenueService.bulkUpsert(items);
      }

      // Save dirty customer revenues
      const dirtyKeys = Object.keys(dirtyCustomers);
      if (dirtyKeys.length > 0) {
        const custItems = [];
        for (const key of dirtyKeys) {
          const [monthStr, clientId] = key.split('-');
          const month = parseInt(monthStr);
          const row = data.find(r => r.month === month);
          const custEntry = row?.customers?.find(c => c.client_id === clientId);
          if (custEntry) {
            custItems.push({
              year, month, client_id: clientId,
              revenue: parseFloat(custEntry.revenue) || 0,
              notes: custEntry.notes || null,
            });
          }
        }
        if (custItems.length > 0) {
          await revenueService.bulkUpsertCustomer(custItems);
        }
      }

      setDirtyTargets({});
      setDirtyCustomers({});
      toast.success('Dati salvati');
      load();
    } catch { toast.error('Errore salvataggio'); } finally { setSaving(false); }
  };

  const computed = useMemo(() => {
    return data.map(r => {
      const t = r.target_value !== null && r.target_value !== '' ? parseFloat(r.target_value) : null;
      const a = r.actual_value !== null && r.actual_value !== '' ? parseFloat(r.actual_value) : null;
      const dev = (t !== null && a !== null) ? a - t : null;
      const pct = (t !== null && a !== null && t !== 0) ? (a - t) / t : null;
      return { ...r, target_num: t, actual_num: a, deviation: dev, pct };
    });
  }, [data]);

  const summary = useMemo(() => {
    let totalTarget = 0, totalActual = 0;
    computed.forEach(r => {
      if (r.target_num !== null) totalTarget += r.target_num;
      if (r.actual_num !== null) totalActual += r.actual_num;
    });
    const diff = totalActual - totalTarget;
    const pct = totalTarget !== 0 ? diff / totalTarget : null;
    return { totalTarget, totalActual, diff, pct };
  }, [computed]);

  // Aggregate customer revenue for the whole year (for pie chart)
  const yearlyCustomerData = useMemo(() => {
    const clientTotals = {};
    data.forEach(r => {
      (r.customers || []).forEach(c => {
        if (!clientTotals[c.client_id]) {
          clientTotals[c.client_id] = { client_id: c.client_id, client_name: c.client_name, client_code: c.client_code, total: 0 };
        }
        clientTotals[c.client_id].total += parseFloat(c.revenue) || 0;
      });
    });
    const arr = Object.values(clientTotals).filter(c => c.total > 0).sort((a, b) => b.total - a.total);
    const grandTotal = arr.reduce((s, c) => s + c.total, 0);
    return arr.map(c => ({ ...c, percentage: grandTotal > 0 ? (c.total / grandTotal * 100) : 0 }));
  }, [data]);

  const trend = useMemo(() => {
    const recent = computed.filter(r => r.actual_num !== null).slice(-3);
    if (recent.length < 2) return { label: 'Dati insufficienti', icon: Minus, color: 'text-slate-500' };
    let ups = 0, downs = 0;
    for (let i = 1; i < recent.length; i++) {
      if (recent[i].actual_num > recent[i - 1].actual_num) ups++;
      else if (recent[i].actual_num < recent[i - 1].actual_num) downs++;
    }
    if (ups > downs) return { label: 'Trend in crescita', icon: TrendingUp, color: 'text-emerald-600' };
    if (downs > ups) return { label: 'Trend in calo', icon: TrendingDown, color: 'text-red-600' };
    return { label: 'Trend stabile', icon: Minus, color: 'text-amber-600' };
  }, [computed]);

  const chartData = useMemo(() => {
    return computed.map(r => ({ name: MONTHS[r.month - 1], Obiettivo: r.target_num, Reale: r.actual_num }));
  }, [computed]);

  const hasDirty = Object.keys(dirtyTargets).length > 0 || Object.keys(dirtyCustomers).length > 0;

  return (
    <div className="space-y-5" data-testid="revenue-kpi-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="revenue-title">IND001 – Analisi Fatturato</h1>
          <p className="text-sm text-slate-500 mt-0.5">Fatturato obiettivo vs reale (calcolato da fatturato clienti)</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-1">
            <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-prev">
              <ChevronLeft className="w-4 h-4 text-slate-600" />
            </button>
            <span className="text-sm font-bold text-slate-900 min-w-[4rem] text-center" data-testid="year-display">{year}</span>
            <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-slate-100 rounded" data-testid="year-next">
              <ChevronRight className="w-4 h-4 text-slate-600" />
            </button>
          </div>
          {canEdit && hasDirty && (
            <Button size="sm" onClick={saveAll} disabled={saving} data-testid="save-all-btn">
              <Save className="w-4 h-4 mr-1" />{saving ? 'Salvataggio...' : 'Salva tutto'}
            </Button>
          )}
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="summary-kpis">
        <SummaryCard label="Fatturato Reale" value={fmtCurrency(summary.totalActual)} unit="EUR" testId="kpi-actual" />
        <SummaryCard label="Fatturato Obiettivo" value={fmtCurrency(summary.totalTarget)} unit="EUR" testId="kpi-target" />
        <SummaryCard label="Scostamento Totale" value={fmtCurrency(summary.diff)} unit="EUR"
          accent={summary.diff >= 0 ? 'text-emerald-600' : 'text-red-600'} testId="kpi-diff" />
        <SummaryCard label="% Scostamento" value={fmtPct(summary.pct)}
          accent={summary.pct !== null && summary.pct >= 0 ? 'text-emerald-600' : 'text-red-600'} testId="kpi-pct" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Line Chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-4" data-testid="revenue-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-slate-700">Andamento Fatturato {year}</h2>
            <div className={`flex items-center gap-1.5 text-sm font-medium ${trend.color}`} data-testid="trend-indicator">
              <trend.icon className="w-4 h-4" />
              <span>{trend.label}</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickFormatter={v => fmtCurrency(v)} />
              <Tooltip
                formatter={(value) => [fmtCurrency(value) + ' EUR', undefined]}
                labelStyle={{ fontWeight: 600 }}
                contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
              />
              <Legend wrapperStyle={{ fontSize: '13px' }} />
              <Line type="monotone" dataKey="Obiettivo" stroke="#94a3b8" strokeWidth={2} strokeDasharray="6 3" dot={{ r: 3 }} connectNulls />
              <Line type="monotone" dataKey="Reale" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: '#0f172a' }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart - Customer Revenue Incidence */}
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="customer-pie-chart">
          <h2 className="text-sm font-semibold text-slate-700 mb-3">Incidenza Fatturato Clienti {year}</h2>
          {yearlyCustomerData.length === 0 ? (
            <div className="flex items-center justify-center h-[260px] text-slate-400 text-sm">Nessun dato cliente</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={yearlyCustomerData}
                    dataKey="total"
                    nameKey="client_name"
                    cx="50%" cy="50%"
                    outerRadius={80}
                    innerRadius={40}
                  >
                    {yearlyCustomerData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value) => fmtCurrency(value) + ' EUR'}
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1 mt-2">
                {yearlyCustomerData.map((c, i) => (
                  <div key={c.client_id} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                      <span className="text-slate-700 truncate max-w-[120px]">{c.client_name}</span>
                    </div>
                    <span className="font-mono text-slate-600">{c.percentage.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Monthly Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="revenue-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <th className="px-4 py-2.5 w-8"></th>
                  <th className="px-4 py-2.5 w-32">Mese</th>
                  <th className="px-4 py-2.5 w-44 text-right">Fatt. Obiettivo</th>
                  <th className="px-4 py-2.5 w-44 text-right">Fatt. Reale Totale</th>
                  <th className="px-4 py-2.5 w-36 text-right">Scostamento</th>
                  <th className="px-4 py-2.5 w-28 text-right">% Scost.</th>
                  <th className="px-4 py-2.5 w-16 text-center">Clienti</th>
                  <th className="px-4 py-2.5">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {computed.map(r => {
                  const isDirty = dirtyTargets[r.month];
                  const isExpanded = expandedMonth === r.month;
                  const customerCount = (r.customers || []).length;
                  return (
                    <React.Fragment key={r.month}>
                      <tr
                        className={`transition-colors cursor-pointer ${isDirty ? 'bg-amber-50' : 'hover:bg-slate-50'} ${isExpanded ? 'bg-slate-50' : ''}`}
                        data-testid={`row-${r.month}`}
                        onClick={() => setExpandedMonth(isExpanded ? null : r.month)}
                      >
                        <td className="px-4 py-2 text-slate-400">
                          <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                        </td>
                        <td className="px-4 py-2 font-medium text-slate-700">{MONTH_FULL[r.month - 1]}</td>
                        <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                          {canEdit ? (
                            <input type="number" step="any"
                              value={r.target_value !== null && r.target_value !== undefined ? r.target_value : ''}
                              onChange={e => handleTargetChange(r.month, 'target_value', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md font-mono text-right focus:ring-2 focus:ring-slate-900 focus:outline-none"
                              placeholder="-" data-testid={`target-${r.month}`} />
                          ) : <span className="font-mono block text-right">{fmtCurrency(r.target_num)}</span>}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className="font-mono text-sm font-semibold text-slate-900" data-testid={`actual-${r.month}`}>
                            {r.actual_num !== null ? fmtCurrency(r.actual_num) : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className={`font-mono text-sm ${r.deviation !== null ? (r.deviation >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`} data-testid={`dev-${r.month}`}>
                            {r.deviation !== null ? (r.deviation >= 0 ? '+' : '') + fmtCurrency(r.deviation) : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <span className={`font-mono text-sm ${r.pct !== null ? (r.pct >= 0 ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`} data-testid={`pct-${r.month}`}>
                            {fmtPct(r.pct)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium ${customerCount > 0 ? 'bg-blue-50 text-blue-700' : 'text-slate-400'}`}>
                            <Users className="w-3 h-3" />{customerCount}
                          </span>
                        </td>
                        <td className="px-4 py-2" onClick={e => e.stopPropagation()}>
                          {canEdit ? (
                            <input type="text"
                              value={r.notes || ''}
                              onChange={e => handleTargetChange(r.month, 'notes', e.target.value)}
                              className="w-full px-2 py-1 text-sm border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-900 focus:outline-none"
                              placeholder="Note..." data-testid={`notes-${r.month}`} />
                          ) : <span className="text-slate-500">{r.notes || '-'}</span>}
                        </td>
                      </tr>
                      {/* Expanded Customer Breakdown */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="bg-slate-50/70 px-4 py-3 border-t border-slate-100">
                            <CustomerBreakdown
                              month={r.month}
                              year={year}
                              customers={r.customers || []}
                              allClients={clients}
                              canEdit={canEdit}
                              onRevenueChange={handleCustomerRevenue}
                              onNotesChange={handleCustomerNotes}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold text-sm border-t-2 border-slate-300">
                  <td className="px-4 py-2.5"></td>
                  <td className="px-4 py-2.5 text-slate-900">Totale</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-700" data-testid="total-target">{fmtCurrency(summary.totalTarget)}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-900" data-testid="total-actual">{fmtCurrency(summary.totalActual)}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`font-mono ${summary.diff >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="total-dev">
                      {summary.diff >= 0 ? '+' : ''}{fmtCurrency(summary.diff)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`font-mono ${summary.pct !== null && summary.pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="total-pct">
                      {fmtPct(summary.pct)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5"></td>
                  <td className="px-4 py-2.5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function CustomerBreakdown({ month, year, customers, allClients, canEdit, onRevenueChange, onNotesChange }) {
  const [showInactive, setShowInactive] = useState(false);
  // Build a merged list: ALL clients (active by default) + their saved revenue if any.
  const savedByClient = useMemo(() => {
    const map = {};
    (customers || []).forEach(c => { map[c.client_id] = c; });
    return map;
  }, [customers]);

  const visibleClients = useMemo(() => {
    return (allClients || []).filter(c => showInactive || c.is_active !== false);
  }, [allClients, showInactive]);

  const rows = useMemo(() => visibleClients.map(c => {
    const saved = savedByClient[c.id];
    return {
      client_id: c.id,
      client_code: c.code,
      client_name: c.name,
      is_active: c.is_active !== false,
      revenue: saved ? saved.revenue : '',
      notes: saved ? (saved.notes || '') : '',
      saved: !!saved,
    };
  }), [visibleClients, savedByClient]);

  const monthTotal = rows.reduce((sum, c) => sum + (parseFloat(c.revenue) || 0), 0);

  return (
    <div data-testid={`customer-breakdown-${month}`}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
          Fatturato per Cliente – {MONTH_FULL[month - 1]} <span className="text-slate-400 ml-2 normal-case">({rows.length} clienti{showInactive ? ' inclusi inattivi' : ' attivi'})</span>
        </h3>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer select-none" data-testid={`toggle-inactive-${month}`}>
          <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-3 h-3" />
          Includi inattivi
        </label>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-slate-400 italic">Nessun cliente in anagrafica</p>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-slate-500 uppercase tracking-wide">
              <th className="py-1 pr-3">Cliente</th>
              <th className="py-1 pr-3 text-right w-40">Fatturato</th>
              <th className="py-1 pr-3 text-right w-24">% sul Totale</th>
              <th className="py-1 pr-3">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(c => {
              const pct = monthTotal > 0 ? (parseFloat(c.revenue) || 0) / monthTotal * 100 : 0;
              return (
                <tr key={c.client_id} className={`hover:bg-white/60 ${!c.is_active ? 'opacity-60' : ''}`}>
                  <td className="py-1.5 pr-3">
                    <span className="font-medium text-slate-700">{c.client_code}</span>
                    <span className="text-slate-500 ml-1.5">{c.client_name}</span>
                    {!c.is_active && <span className="ml-2 text-[10px] text-amber-600">(inattivo)</span>}
                  </td>
                  <td className="py-1.5 pr-3">
                    {canEdit ? (
                      <input type="number" step="any"
                        value={c.revenue !== null && c.revenue !== undefined ? c.revenue : ''}
                        onChange={e => onRevenueChange(month, c.client_id, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md font-mono text-right focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        placeholder="0"
                        data-testid={`cust-rev-${month}-${c.client_id}`}
                      />
                    ) : (
                      <span className="font-mono block text-right">{fmtCurrency(c.revenue)}</span>
                    )}
                  </td>
                  <td className="py-1.5 pr-3 text-right font-mono text-slate-500">{pct.toFixed(1)}%</td>
                  <td className="py-1.5 pr-3">
                    {canEdit ? (
                      <input type="text"
                        value={c.notes}
                        onChange={e => onNotesChange && onNotesChange(month, c.client_id, e.target.value)}
                        onClick={e => e.stopPropagation()}
                        className="w-full px-2 py-1 text-xs border border-slate-200 rounded-md focus:ring-2 focus:ring-slate-900 focus:outline-none"
                        placeholder="Note..."
                        data-testid={`cust-notes-${month}-${c.client_id}`}
                      />
                    ) : (
                      <span className="text-slate-500">{c.notes || '-'}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t border-slate-200 font-semibold">
              <td className="py-1.5 pr-3 text-slate-700">Totale Mese</td>
              <td className="py-1.5 pr-3 text-right font-mono text-slate-900">{fmtCurrency(monthTotal)}</td>
              <td className="py-1.5 pr-3 text-right font-mono text-slate-500">100%</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

function SummaryCard({ label, value, unit, accent, testId }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3" data-testid={testId}>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
      <p className={`text-xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
      {unit && <p className="text-xs text-slate-400 mt-0.5">{unit}</p>}
    </div>
  );
}
